import { MessageKey, MessageParams } from '../i18n';
import { BikeConfig, DEFAULT_BIKE } from '../sim/config';
import { isWheelspin } from '../sim/grip';
import { COACH_CONFIG } from './config';
import { DrillEvent } from './drill';
import { FrameState } from './frame';

const NO_EVENTS: readonly DrillEvent[] = [];
/** Coach line per limit crossed while recovering (src/sim/physics/limits.ts FallCause). */
const RECOVERY_KEYS = { landing: 'coach.recover.landing', tipped: 'coach.recover.lean', washout: 'coach.recover.brake', overBars: 'coach.recover.brake', spin: 'coach.recover.throttle', hit: 'coach.hitObstacle' } as const satisfies Record<string, MessageKey>;

export interface CoachLine { key: MessageKey; time: number; params?: MessageParams }

/** Situations that must last a moment before the coach speaks about them (s of sim time held). */
type Held = 'wheelie' | 'footOut' | 'airHigh' | 'airLow' | 'climb' | 'descent' | 'sand';

/**
 * Turns sim telemetry and drill events into short coach lines. Rules are checked in priority order (fall, front
 * lock, a hit obstacle or cone, front brake on a slippery surface or at lean, missed cone, nose high / low in the air, rear slip, wheelie,
 * climb, descent, sand, foot out, stance); at most one line per `minInterval` seconds of sim time, anything else in
 * between is dropped. Lines are message keys, the UI localises them. The rules follow the instructor's notes
 * (docs/research/2026-09-28-coach-notes.md).
 */
export class Coach {
  private last = -Infinity;
  private held: Record<Held, number> = { wheelie: 0, footOut: 0, airHigh: 0, airLow: 0, climb: 0, descent: 0, sand: 0 };
  private fallSaid = false;

  constructor(readonly cfg: typeof COACH_CONFIG = COACH_CONFIG, readonly bike: BikeConfig = DEFAULT_BIKE) {}

  update(f: FrameState, events: readonly DrillEvent[] = NO_EVENTS): CoachLine | null {
    const tel = f.bike.telemetry;
    if (!tel.fallen) this.fallSaid = false;
    this.track(f);

    const key = this.pick(f, events);
    if (!key || f.time - this.last < this.cfg.minInterval) return null;
    this.last = f.time;
    if (key === 'coach.fall') this.fallSaid = true;
    for (const [k, rule] of Object.entries(HELD_KEYS) as [Held, MessageKey][]) if (rule === key) this.held[k] = 0;
    return { key, time: f.time };
  }

  /** Advances the "held for a while" timers. */
  private track(f: FrameState): void {
    const b = f.bike, tel = b.telemetry, i = f.input, c = this.cfg;
    const rear = b.wheels[0], front = b.wheels[1];
    const grounded = !tel.airborne;
    const speed = Math.abs(tel.speed);
    // (Plain assignments, no closure: this runs every physics step and must not allocate.)
    const h = this.held;
    h.wheelie = rear.contact && !front.contact && grounded ? h.wheelie + f.dt : 0;
    h.footOut = grounded && !i.autoFoot && b.rider.foot < 0.5 && speed > c.footMinSpeed && speed < this.bike.footMaxSpeed
      && Math.abs(tel.roll) > c.footLean ? h.footOut + f.dt : 0;
    h.airHigh = tel.airborne && tel.pitch > c.airNoseHigh && (i.rearBrake ?? 0) < 0.2 ? h.airHigh + f.dt : 0;
    h.airLow = tel.airborne && tel.pitch < -c.airNoseLow && i.throttle < 0.3 ? h.airLow + f.dt : 0;
    h.climb = grounded && tel.pitch > c.slopePitch && i.body < 0.3 && i.throttle > 0.4 ? h.climb + f.dt : 0;
    h.descent = grounded && tel.pitch < -c.slopePitch && i.body > -0.3 && speed > 2 ? h.descent + f.dt : 0;
    h.sand = grounded && f.surface === 'sand' && i.throttle < 0.1 && speed > 3 ? h.sand + f.dt : 0;
  }

  private pick(f: FrameState, events: readonly DrillEvent[]): MessageKey | null {
    const tel = f.bike.telemetry, c = this.cfg, h = this.held;
    // Simplified mode: the game does the brake split, air attitude, body, stance and foot, so no lines about them.
    const auto = !!f.input.assist;
    if (tel.fallen) return this.fallSaid ? null : 'coach.fall';
    // noCrash: a limit crossed is a recovery with a message, named by what went wrong (owner 2026-10-06, point 11).
    if (tel.recovery) return RECOVERY_KEYS[tel.recovery];
    if (tel.frontLock) return 'coach.frontLock';
    if ((tel.hit && tel.hit.speed > c.hitSpeed) || hasEvent(events, 'hitCone')) return 'coach.hitObstacle';
    // Near the limits (owner 2026-10-06: a medium mistake is a warning and the ride goes on, not a fall).
    if (tel.landing && tel.landing.verdict === 'hard') return 'coach.hardLanding';
    if (!tel.airborne && Math.abs(tel.roll) > c.warnLean) return 'coach.tooMuchLean';
    if (!tel.airborne && tel.sideslip > c.warnSlide) return f.input.throttle > 0.6 ? 'coach.tooMuchThrottle' : 'coach.unstable';
    const slippery = f.surface === 'mud' || f.surface === 'water' || f.surface === 'roots' || f.surface === 'grass';
    if (!auto && tel.frontBrake > c.hardFrontBrake * this.bike.frontBrakeTorque && (slippery || Math.abs(tel.roll) > c.brakeLean)) return 'coach.mudFrontBrake';
    if (hasEvent(events, 'missedCone')) return 'coach.missedCone';
    if (!auto && h.airHigh >= c.airTime) return 'coach.airRearBrake';
    if (!auto && h.airLow >= c.airTime) return 'coach.airThrottle';
    if (isWheelspin(tel)) return 'coach.rearSlip';
    if (h.wheelie >= c.wheelieTime && f.input.trick !== 1) return 'coach.wheelie'; // (not an asked lean-back wheelie)
    if (!auto && h.climb >= c.slopeTime) return 'coach.climbForward';
    if (!auto && h.descent >= c.slopeTime) return 'coach.descentBack';
    if (h.sand >= c.sandTime) return 'coach.sandThrottle';
    if (h.footOut >= c.footTime) return 'coach.footOut';
    for (let k = 0; !auto && k < events.length; k++) {
      const e = events[k]!;
      if (e.type === 'wrongStance') return e.expected === 'standing' ? 'coach.standUp' : 'coach.sitDown';
    }
    return null;
  }
}

/** Whether `events` has one of `type` (a loop: this runs every step and must not allocate a callback). */
function hasEvent(events: readonly DrillEvent[], type: DrillEvent['type']): boolean {
  for (let k = 0; k < events.length; k++) if (events[k]!.type === type) return true;
  return false;
}

/** Which timer a line resets once it has been said. */
const HELD_KEYS: Record<Held, MessageKey> = {
  wheelie: 'coach.wheelie', footOut: 'coach.footOut', airHigh: 'coach.airRearBrake', airLow: 'coach.airThrottle',
  climb: 'coach.climbForward', descent: 'coach.descentBack', sand: 'coach.sandThrottle',
};
